// src/hooks/useWord.ts
import { wordApi } from '../api/wordApi';
import {
    BulkCreateWordsRequest,
    UpdateWordRequest,
    ReorderWordsRequest,
} from '../models/word/wordRequests.ts';
import {
    BulkCreateWordsResponse,
    BulkDeleteWordsResponse,
    BulkUpdateDifficultyResponse,
} from '../models/word/wordResponse.ts';
import {AdminVocabResponse, AiTopicDifficulty} from '../models/word/aiVocabModels.ts';
import {
    useBaseQuery,
    useBaseMutation,
    useNoDataMutation,
    useInvalidateQueries
} from './useBaseQuery';
import { wordbookKeys } from './useWordbook';
import {PaginationData} from "../models/paginationData.ts";
import type {
    DictionaryMetadataBackfillReq,
    DictionaryMetadataBackfillJob,
    DictionaryWordFilterParams,
    DictionaryWordItem,
    DictionaryWordBulkUpsertReq,
    DictionaryWordBulkUpsertRes,
    BritishPhoneticPreview,
} from "../types/dictionary.ts";

// Query Keys
export const wordKeys = {
    all: ['word'] as const,
    dictionary: () => [...wordKeys.all, 'dictionary'] as const,
    dictionaryList: (params: DictionaryWordFilterParams) => [...wordKeys.dictionary(), params] as const,
    dictionaryBackfillJobs: () => [...wordKeys.dictionary(), 'backfill-jobs'] as const,
    dictionaryBackfillJobList: (page: number, size: number) => [...wordKeys.dictionaryBackfillJobs(), page, size] as const,
};

// 단어 일괄 생성
export const useBulkCreateWords = () => {
    const { invalidateMultiple } = useInvalidateQueries();

    return useBaseMutation<BulkCreateWordsRequest, BulkCreateWordsResponse>(
        (data) => wordApi.bulkCreate(data),
        {
            onSuccess: (_, variables) => {
                invalidateMultiple([
                    wordbookKeys.detail(variables.wordbookId),
                    wordbookKeys.lists(),
                ]);
            },
        }
    );
};

// AI 단어 생성: 단어 목록
export const useAiGenerateFromList = () => {
    return useBaseMutation<
        { wordbookId: number; words: string[]; wordLang: string; meaningLang: string },
        AdminVocabResponse
    >((vars) => wordApi.aiGenerateFromList(vars.wordbookId, {
        words: vars.words,
        wordLang: vars.wordLang,
        meaningLang: vars.meaningLang,
    }));
};

// AI 단어 생성: 주제
export const useAiGenerateFromTopic = () => {
    return useBaseMutation<
        {
            wordbookId: number;
            topic: string;
            wordLang: string;
            meaningLang: string;
            wordCount: number;
            difficulty: AiTopicDifficulty;
        },
        AdminVocabResponse
    >((vars) => wordApi.aiGenerateFromTopic(vars.wordbookId, {
        topic: vars.topic,
        wordLang: vars.wordLang,
        meaningLang: vars.meaningLang,
        wordCount: vars.wordCount,
        difficulty: vars.difficulty,
    }));
};

// AI 단어 추출: 이미지
export const useAiExtractFromImage = () => {
    return useBaseMutation<
        { wordbookId: number; image: File; wordLang: string; meaningLang: string },
        AdminVocabResponse
    >((vars) => wordApi.aiExtractFromImage(vars.wordbookId, vars.image, vars.wordLang, vars.meaningLang));
};

// 단어 수정
export const useUpdateWord = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<{ wordId: number; wordbookId: number; data: UpdateWordRequest }>(
        ({ wordId, data }) => wordApi.update(wordId, data),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

// 단어 삭제
export const useDeleteWord = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<{ wordId: number; wordbookId: number }>(
        ({ wordId }) => wordApi.delete(wordId),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

// 단어 영구 삭제
export const usePermanentDeleteWord = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<{ wordId: number; wordbookId: number }>(
        ({ wordId }) => wordApi.permanentDelete(wordId),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

// 단어 복원
export const useRestoreWord = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<{ wordId: number; wordbookId: number }>(
        ({ wordId }) => wordApi.restore(wordId),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

// 단어 일괄 삭제
export const useBulkDeleteWords = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<{ wordIds: number[]; wordbookId: number }, BulkDeleteWordsResponse>(
        ({ wordIds }) => wordApi.bulkDelete(wordIds),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

// 단어 일괄 난이도 변경
export const useBulkUpdateDifficulty = () => {
    const { invalidate } = useInvalidateQueries();

    return useBaseMutation<
        { wordIds: number[]; difficulty: number; wordbookId: number },
        BulkUpdateDifficultyResponse
    >(
        ({ wordIds, difficulty }) => wordApi.bulkUpdateDifficulty(wordIds, difficulty),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

// 단어 순서 재정렬
export const useReorderWords = () => {
    const { invalidate } = useInvalidateQueries();

    return useNoDataMutation<ReorderWordsRequest>(
        (data) => wordApi.reorder(data),
        {
            onSuccess: (_, variables) => {
                invalidate(wordbookKeys.detail(variables.wordbookId));
            },
        }
    );
};

export const useDictionaryWords = (params: DictionaryWordFilterParams) => {
    return useBaseQuery<PaginationData<DictionaryWordItem>>(
        wordKeys.dictionaryList(params),
        () => wordApi.getDictionaryWords(params),
        {staleTime: 30 * 1000}
    );
};

export const useBackfillDictionaryMetadata = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<DictionaryMetadataBackfillReq, DictionaryMetadataBackfillJob>(
        (data) => wordApi.backfillDictionaryMetadata(data),
        {
            onSuccess: () => {
                invalidateMultiple([
                    wordKeys.dictionary(),
                    wordKeys.dictionaryBackfillJobs(),
                ]);
            },
        }
    );
};

export const useDictionaryBackfillJobs = (page: number = 1, size: number = 20, inProgressOnly: boolean = false) => {
    return useBaseQuery<PaginationData<DictionaryMetadataBackfillJob>>(
        [...wordKeys.dictionaryBackfillJobList(page, size), inProgressOnly],
        () => wordApi.getDictionaryBackfillJobs(page, size, inProgressOnly),
        {staleTime: 5000}
    );
};

export const useCancelDictionaryBackfillJob = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<number, DictionaryMetadataBackfillJob>(
        (jobId) => wordApi.cancelDictionaryBackfillJob(jobId),
        {
            onSuccess: () => {
                invalidateMultiple([
                    wordKeys.dictionaryBackfillJobs(),
                    wordKeys.dictionary(),
                ]);
            },
        }
    );
};

// 영국식 → 미국식 재생성 dry-run (버튼 클릭 시 on-demand 호출)
export const usePreviewRegeneratePhoneticUs = () => {
    return useBaseMutation<void, BritishPhoneticPreview>(
        () => wordApi.previewRegeneratePhoneticUs(),
    );
};

// 영국식 → 미국식 재생성 잡 트리거 (재생성 잡도 같은 DICTIONARY_METADATA_BACKFILL jobType)
export const useRegeneratePhoneticUs = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<DictionaryMetadataBackfillReq, DictionaryMetadataBackfillJob>(
        (data) => wordApi.regeneratePhoneticUs(data),
        {
            onSuccess: () => {
                invalidateMultiple([
                    wordKeys.dictionary(),
                    wordKeys.dictionaryBackfillJobs(),
                ]);
            },
        }
    );
};

export const useExportDictionaryWords = () => {
    return useBaseMutation<{word?: string; languageCode?: string; onlyMissing?: boolean; limit?: number}, DictionaryWordItem[]>(
        (params) => wordApi.exportDictionaryWords(params),
    );
};

export const useUpdateDictionaryWord = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<{ id: number; data: { translation?: string; translationLang?: string; pos?: string; difficultyScore?: number } }, DictionaryWordItem>(
        ({id, data}) => wordApi.updateDictionaryWord(id, data),
        {
            onSuccess: () => {
                invalidateMultiple([wordKeys.dictionary()]);
            },
        },
    );
};

export const useBulkUpsertDictionaryWords = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<DictionaryWordBulkUpsertReq, DictionaryWordBulkUpsertRes>(
        (data) => wordApi.bulkUpsertDictionaryWords(data),
        {
            onSuccess: () => {
                invalidateMultiple([wordKeys.dictionary()]);
            },
        },
    );
};

export const useResetDictionaryBackfillAttempts = () => {
    const {invalidateMultiple} = useInvalidateQueries();
    return useBaseMutation<number, DictionaryWordItem>(
        (id) => wordApi.resetDictionaryBackfillAttempts(id),
        {
            onSuccess: () => {
                invalidateMultiple([wordKeys.dictionary()]);
            },
        },
    );
};
